/*
 * BLOOM: TASK-027 — cart drawer.
 * Ported from Shopify Dawn v7.0.0 (assets/cart-drawer.js + drawer parts of assets/cart.js, MIT license)
 * and made self-contained for Sense 3.0.0: Sense cart.js (CartItems) only knows the /cart page markup.
 * Markup: snippets/bloom-cart-drawer.liquid. Section Rendering API: sections/bloom-cart-drawer.liquid.
 * Uses Sense global.js helpers: trapFocus, removeTrapFocus, debounce, fetchConfig, quantity-input.
 */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.onOverlayClick = this.close.bind(this);
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.bindOverlay();
        this.setHeaderCartIconAccessibility();
      }

      bindOverlay() {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.onOverlayClick);
      }

      setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink) return;

        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.code && event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        // The animation is not always triggered without a timeout (Dawn).
        setTimeout(() => {
          this.classList.add('animate', 'active');
        });

        this.addEventListener(
          'transitionend',
          () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
        );

        document.body.classList.add('overflow-hidden');
      }

      close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');

        // On the /cart page the main cart list is not re-rendered by the drawer: reload it after drawer changes.
        if (this.cartChanged && document.getElementById('main-cart-items')) {
          this.cartChanged = false;
          window.location.reload();
        }
      }

      markCartChanged() {
        this.cartChanged = true;
      }

      renderContents(parsedState) {
        this.classList.remove('is-empty');
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });
        this.markCartChanged();

        setTimeout(() => {
          this.bindOverlay();
          this.open();
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      getSectionsToRender() {
        return [
          {
            id: 'bloom-cart-drawer',
            selector: '#CartDrawer',
          },
          {
            id: 'cart-icon-bubble',
          },
        ];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}

if (!customElements.get('bloom-cart-drawer-remove')) {
  customElements.define(
    'bloom-cart-drawer-remove',
    class BloomCartDrawerRemove extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItems = this.closest('bloom-cart-drawer-items');
          if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
        });
      }
    }
  );
}

if (!customElements.get('bloom-cart-drawer-items')) {
  customElements.define(
    'bloom-cart-drawer-items',
    class BloomCartDrawerItems extends HTMLElement {
      constructor() {
        super();

        this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
          (total, quantityInput) => total + parseInt(quantityInput.value),
          0
        );

        this.debouncedOnChange = debounce((event) => {
          this.onChange(event);
        }, 300);

        this.addEventListener('change', this.debouncedOnChange.bind(this));
      }

      onChange(event) {
        if (!event.target.dataset.index) return;
        this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'bloom-cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      updateQuantity(line, quantity, name) {
        this.enableLoading(line);

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);

            // 422 (e.g. not enough stock): keep the current drawer, restore the quantity, show the message.
            if (parsedState.errors) {
              const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
              if (quantityElement) quantityElement.value = quantityElement.defaultValue;
              const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
              if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = parsedState.errors;
              this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
              return;
            }

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
              cartDrawer.markCartChanged();
            }

            this.getSectionsToRender().forEach((section) => {
              const container = document.getElementById(section.id);
              if (!container) return;
              const elementToReplace = container.querySelector(section.selector) || container;
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            });

            this.updateLiveRegions(line, parsedState.item_count);

            // The drawer content was replaced: move focus to a sensible element inside the new markup.
            const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
              trapFocus(cartDrawer, lineItem.querySelector(`[name="${name}"]`));
            } else if (parsedState.item_count === 0 && cartDrawer) {
              trapFocus(cartDrawer.querySelector('.drawer__inner-empty'), cartDrawer.querySelector('.drawer__inner-empty a'));
            } else if (cartDrawer && cartDrawer.querySelector('.cart-item')) {
              trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__name'));
            }
          })
          .catch(() => {
            this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
            const errors = document.getElementById('CartDrawer-CartErrors');
            if (errors) errors.textContent = window.cartStrings.error;
          })
          .finally(() => {
            this.disableLoading();
          });
      }

      updateLiveRegions(line, itemCount) {
        if (this.currentItemCount === itemCount) {
          const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
          if (lineItemError && quantityElement) {
            lineItemError.querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace(
              '[quantity]',
              quantityElement.value
            );
          }
        }

        this.currentItemCount = itemCount;

        const lineItemStatus = document.getElementById('CartDrawer-LineItemStatus');
        if (lineItemStatus) lineItemStatus.setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById('CartDrawer-LiveRegionText');
        if (!cartStatus) return;
        cartStatus.setAttribute('aria-hidden', false);
        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      enableLoading(line) {
        const cartItems = document.getElementById('CartDrawer-CartItems');
        if (cartItems) cartItems.classList.add('cart__items--disabled');

        this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`).forEach((overlay) =>
          overlay.classList.remove('hidden')
        );

        document.activeElement.blur();
        const lineItemStatus = document.getElementById('CartDrawer-LineItemStatus');
        if (lineItemStatus) lineItemStatus.setAttribute('aria-hidden', false);
      }

      disableLoading() {
        const cartItems = document.getElementById('CartDrawer-CartItems');
        if (cartItems) cartItems.classList.remove('cart__items--disabled');
      }
    }
  );
}
